/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { getEventPosition, getChromaColor } from '../utils';
import { DEFAULT_VISUALIZATION_COLOR } from '../../../services';
import { ColorStop } from './color_stop_thumb';

const EUI_THUMB_SIZE = 16; // Same as $euiRangeThumbHeight & $euiRangeThumbWidth

export const removeStop = (colorStops: ColorStop[], index: number) => {
  if (colorStops.length === 1) {
    return colorStops;
  }

  return [...colorStops.slice(0, index), ...colorStops.slice(index + 1)];
};

export const addDefinedStop = (
  colorStops: ColorStop[],
  stop: ColorStop['stop'],
  color: ColorStop['color'] = DEFAULT_VISUALIZATION_COLOR
) => {
  const newStop = {
    stop,
    color,
  };
  return [...colorStops, newStop];
};

export const addStop = (
  colorStops: ColorStop[],
  color: ColorStop['color'] = DEFAULT_VISUALIZATION_COLOR,
  max: number
) => {
  const index = colorStops.length ? colorStops.length - 1 : 0;
  const stops = colorStops.map(el => el.stop);
  const currentStop = stops[index] != null ? stops[index] : max;
  let delta = 1;
  if (index !== 0) {
    const prevStop = stops[index - 1];
    delta = currentStop - prevStop;
  }

  let stop = currentStop + delta;

  if (stop > max) {
    stop = max;
  }

  // We've reached the max, so start working backwards
  while (stops.indexOf(stop) > -1) {
    stop--;
  }

  const newStop = {
    stop,
    color,
  };
  return [
    ...colorStops.slice(0, index + 1),
    newStop,
    ...colorStops.slice(index + 1),
  ];
};

export const isColorInvalid = (color: string, showAlpha: boolean = false) => {
  return getChromaColor(color, showAlpha) == null || color === '';
};

export const isStopInvalid = (stop: ColorStop['stop']) => {
  return stop == null || isNaN(stop);
};

export const isInvalid = (
  colorStops: ColorStop[],
  showAlpha: boolean = false
) => {
  return colorStops.some(colorStop => {
    return (
      isColorInvalid(colorStop.color, showAlpha) ||
      isStopInvalid(colorStop.stop)
    );
  });
};

export const calculateScale = (trackWidth: number) => {
  const thumbToTrackRatio = EUI_THUMB_SIZE / trackWidth;
  return (1 - thumbToTrackRatio) * 100;
};

export const getStopFromMouseLocation = (
  location: { x: number; y: number },
  ref: HTMLDivElement,
  min: number,
  max: number
) => {
  const box = getEventPosition(location, ref);
  return Math.round((box.left / box.width) * (max - min) + min);
};

export const getPositionFromStop = (
  stop: ColorStop['stop'],
  ref: HTMLDivElement,
  min: number,
  max: number
) => {
  // For wide implementations, integer percentages can be visually off.
  // Use 1 decimal place for more accuracy
  return parseFloat(
    (
      ((stop - min) / (max - min)) *
      calculateScale(ref ? ref.clientWidth : 100)
    ).toFixed(1)
  );
};
try {
    // @ts-ignore
    isStopInvalid.displayName = "isStopInvalid";
    // @ts-ignore
    isStopInvalid.__docgenInfo = { "description": "", "displayName": "isStopInvalid", "props": { "toString": { "defaultValue": null, "description": "Returns a string representation of an object.\n@param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.", "name": "toString", "required": false, "type": { "name": "(radix?: number | undefined) => string" } }, "toFixed": { "defaultValue": null, "description": "Returns a string representing a number in fixed-point notation.\n@param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.", "name": "toFixed", "required": true, "type": { "name": "(fractionDigits?: number | undefined) => string" } }, "toExponential": { "defaultValue": null, "description": "Returns a string containing a number represented in exponential notation.\n@param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.", "name": "toExponential", "required": true, "type": { "name": "(fractionDigits?: number | undefined) => string" } }, "toPrecision": { "defaultValue": null, "description": "Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.\n@param precision Number of significant digits. Must be in the range 1 - 21, inclusive.", "name": "toPrecision", "required": true, "type": { "name": "(precision?: number | undefined) => string" } }, "valueOf": { "defaultValue": null, "description": "Returns the primitive value of the specified object.", "name": "valueOf", "required": false, "type": { "name": "() => number" } }, "toLocaleString": { "defaultValue": null, "description": "Converts a number to a string by using the current or specified locale.\n@param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.\n@param options An object that contains one or more properties that specify comparison options.", "name": "toLocaleString", "required": false, "type": { "name": "(locales?: string | string[] | undefined, options?: NumberFormatOptions | undefined) => string" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/color_picker/color_stops/utils.ts#isStopInvalid"] = { docgenInfo: isStopInvalid.__docgenInfo, name: "isStopInvalid", path: "src/components/color_picker/color_stops/utils.ts#isStopInvalid" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    calculateScale.displayName = "calculateScale";
    // @ts-ignore
    calculateScale.__docgenInfo = { "description": "", "displayName": "calculateScale", "props": { "toString": { "defaultValue": null, "description": "Returns a string representation of an object.\n@param radix Specifies a radix for converting numeric values to strings. This value is only used for numbers.", "name": "toString", "required": false, "type": { "name": "(radix?: number | undefined) => string" } }, "toFixed": { "defaultValue": null, "description": "Returns a string representing a number in fixed-point notation.\n@param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.", "name": "toFixed", "required": true, "type": { "name": "(fractionDigits?: number | undefined) => string" } }, "toExponential": { "defaultValue": null, "description": "Returns a string containing a number represented in exponential notation.\n@param fractionDigits Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.", "name": "toExponential", "required": true, "type": { "name": "(fractionDigits?: number | undefined) => string" } }, "toPrecision": { "defaultValue": null, "description": "Returns a string containing a number represented either in exponential or fixed-point notation with a specified number of digits.\n@param precision Number of significant digits. Must be in the range 1 - 21, inclusive.", "name": "toPrecision", "required": true, "type": { "name": "(precision?: number | undefined) => string" } }, "valueOf": { "defaultValue": null, "description": "Returns the primitive value of the specified object.", "name": "valueOf", "required": false, "type": { "name": "() => number" } }, "toLocaleString": { "defaultValue": null, "description": "Converts a number to a string by using the current or specified locale.\n@param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used.\n@param options An object that contains one or more properties that specify comparison options.", "name": "toLocaleString", "required": false, "type": { "name": "(locales?: string | string[] | undefined, options?: NumberFormatOptions | undefined) => string" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/color_picker/color_stops/utils.ts#calculateScale"] = { docgenInfo: calculateScale.__docgenInfo, name: "calculateScale", path: "src/components/color_picker/color_stops/utils.ts#calculateScale" };
}
catch (__react_docgen_typescript_loader_error) { }